import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { RIGHT_PANEL_TABS, RightPanelTab } from '../pages/PlanningPageHelpers';

import type { PageInfo, FlowInfo } from './DesignPanelHelpers';

/* ── Static sub-tab definitions ────────────────────────────────── */

interface SubTabDef { key: string; label: string; icon?: string; }

const TASKS_SUBTABS: SubTabDef[] = [
  { key: 'list', label: 'List', icon: 'fa-solid fa-list' },
  { key: 'gantt', label: 'Gantt', icon: 'fa-solid fa-chart-gantt' },
  { key: 'kanban', label: 'Kanban', icon: 'fa-solid fa-columns' },
  { key: 'sprint', label: 'Sprint', icon: 'fa-solid fa-business-time' },
  { key: 'phases', label: 'Phases', icon: 'fa-solid fa-layer-group' },
];

const ARCH_SUBTABS: SubTabDef[] = [
  { key: 'service_network', label: 'Services', icon: 'fa-solid fa-diagram-project' },
  { key: 'backend_modules', label: 'Backend', icon: 'fa-solid fa-gears' },
  { key: 'frontend_modules', label: 'Frontend', icon: 'fa-solid fa-display' },
  { key: 'sequence', label: 'Sequence', icon: 'fa-solid fa-arrow-right-arrow-left' },
  { key: 'er_diagram', label: 'Database', icon: 'fa-solid fa-database' },
];

const SUB_TAB_CONFIG: Record<string, {
  tabs: SubTabDef[]; dispatchEvent: string; listenEvent: string; defaultKey: string;
}> = {
  tasks: {
    tabs: TASKS_SUBTABS, dispatchEvent: 'switch_tasks_view',
    listenEvent: 'tasks_view_changed', defaultKey: 'list',
  },
  architecture: {
    tabs: ARCH_SUBTABS, dispatchEvent: 'select_architecture_tab',
    listenEvent: 'architecture_tab_changed', defaultKey: 'service_network',
  },
};

/* ── Shared click-outside hook ─────────────────────────────────── */

function useClickOutside(
  ref: React.RefObject<HTMLDivElement | null>, onClose: () => void, active: boolean,
) {
  useEffect(() => {
    if (!active) return undefined;

    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };

    document.addEventListener('mousedown', handler);

    return () => document.removeEventListener('mousedown', handler);
  }, [active, ref, onClose]);
}

/* ── Per-item three-dot menu (reusable for pages & personas) ──── */

function ItemDotMenu({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useClickOutside(ref, () => setOpen(false), open);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen((o) => {
      if (!o && btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect();

        setPos({ top: rect.bottom + 4, left: rect.right - 130 });
      }

      return !o;
    });
  }, []);

  return (
    <div className="navbar-subtab-dots" ref={ref}>
      <button className="navbar-subtab-dots__btn" ref={btnRef} onClick={handleToggle}>⋯</button>
      {open && (
        <div className="navbar-subtab-dots__menu" style={{ position: 'fixed', top: pos.top, left: pos.left }}
          onClick={(e) => { if ((e.target as HTMLElement).closest('button')) setOpen(false); }}>
          {children}
        </div>
      )}
    </div>
  );
}



/* ── Hooks ─────────────────────────────────────────────────────── */

function useActiveTab() {
  const [tab, setTab] = useState<RightPanelTab>('requirements');

  useEffect(() => {
    const h = (e: Event) => setTab((e as globalThis.CustomEvent).detail as RightPanelTab);

    window.addEventListener('active_tab_changed', h);

    return () => window.removeEventListener('active_tab_changed', h);
  }, []);

  return tab;
}

function useStaticSubTabs() {
  const [active, setActive] = useState<Record<string, string>>({
    tasks: 'list', architecture: 'service_network',
  });

  useEffect(() => {
    const handlers: [string, (e: Event) => void][] = Object.entries(SUB_TAB_CONFIG).map(
      ([tab, cfg]) => [cfg.listenEvent, (e: Event) => {
        setActive((prev) => ({ ...prev, [tab]: (e as globalThis.CustomEvent).detail as string }));
      }],
    );

    handlers.forEach(([evt, fn]) => window.addEventListener(evt, fn));

    return () => handlers.forEach(([evt, fn]) => window.removeEventListener(evt, fn));
  }, []);

  const select = useCallback((tab: string, key: string) => {
    const cfg = SUB_TAB_CONFIG[tab];

    if (!cfg) return;
    setActive((prev) => ({ ...prev, [tab]: key }));
    window.dispatchEvent(new globalThis.CustomEvent(cfg.dispatchEvent, { detail: key }));
  }, []);

  return { active, select };
}

function useDynamicData() {
  const [pages, setPages] = useState<PageInfo[]>([]);
  const [flows, setFlows] = useState<FlowInfo[]>([]);
  const [activePage, setActivePage] = useState('');
  const [activeFlow, setActiveFlow] = useState('');

  useEffect(() => {
    const handlers: [string, (e: Event) => void][] = [
      ['design_pages_data', (e) => setPages((e as globalThis.CustomEvent).detail)],
      ['design_page_changed', (e) => setActivePage((e as globalThis.CustomEvent).detail)],
      ['user_flows_data', (e) => setFlows((e as globalThis.CustomEvent).detail)],
      ['user_flow_changed', (e) => setActiveFlow((e as globalThis.CustomEvent).detail)],
    ];

    handlers.forEach(([evt, fn]) => window.addEventListener(evt, fn));

    return () => handlers.forEach(([evt, fn]) => window.removeEventListener(evt, fn));
  }, []);

  return { pages, flows, activePage, activeFlow };
}

/* ── Design action buttons ────────────────────────────────────── */

function DesignActions({ hasPages }: { hasPages: boolean }) {
  return (
    <div className="navbar-design-actions">
      {hasPages && (
        <button
          className="navbar-action-btn"
          onClick={() => window.dispatchEvent(new globalThis.CustomEvent('open_design_fullscreen'))}
          title="Full screen preview"
        >
          <i className="fa-solid fa-expand" />
        </button>
      )}
      <button
        className="navbar-share-btn"
        onClick={() => window.dispatchEvent(new globalThis.CustomEvent('open_share_modal'))}
      >
        <i className="fa-solid fa-share-nodes" />
        <span>Share</span>
      </button>
    </div>
  );
}

/* ── Dot menu item helpers ──────────────────────────────────────── */

function PageDotMenuItems({ p }: { p: PageInfo }) {
  const fire = (action: string) => window.dispatchEvent(new globalThis.CustomEvent(
    'navbar_page_action', { detail: { action, id: p.id, name: p.name } },
  ));

  return (
    <>
      <button className="navbar-subtab-dots__item" onClick={() => fire('download')}>
        <i className="fa-solid fa-download" /> Download
      </button>
      <button className="navbar-subtab-dots__item navbar-subtab-dots__item--danger" onClick={() => fire('delete')}>
        <i className="fa-solid fa-trash" /> Delete
      </button>
      <div className="navbar-subtab-dots__divider" />
      <button className="navbar-subtab-dots__item" onClick={() => fire('versions')}>
        <i className="fa-solid fa-clock-rotate-left" /> Versions
      </button>
    </>
  );
}

function FlowDotMenuItems({ f }: { f: FlowInfo }) {
  const fire = (action: string) => window.dispatchEvent(new globalThis.CustomEvent(
    'navbar_flow_action', { detail: { action, id: f.id, persona: f.persona } },
  ));

  return (
    <>
      <button className="navbar-subtab-dots__item" onClick={() => fire('download')}>
        <i className="fa-solid fa-download" /> Download
      </button>
      <button className="navbar-subtab-dots__item navbar-subtab-dots__item--danger" onClick={() => fire('delete')}>
        <i className="fa-solid fa-trash" /> Delete
      </button>
      <div className="navbar-subtab-dots__divider" />
      <button className="navbar-subtab-dots__item" onClick={() => fire('versions')}>
        <i className="fa-solid fa-clock-rotate-left" /> Versions
      </button>
    </>
  );
}

/* ── Inline sub-tab items (render without wrapper) ─────────────── */

function InlinePageItems({ pages, activeKey, onSelect }: {
  pages: PageInfo[]; activeKey: string; onSelect: (name: string) => void;
}) {
  return (
    <>
      {pages.map((p, i) => {
        const pct = p.sections_total > 0 ? Math.round((p.sections_done / p.sections_total) * 100) : 0;
        const showBar = p.generating && p.sections_total > 0;

        return (
          <div
            key={p.id}
            className={`navbar-subtab-rich navbar-subtab-slide${activeKey === p.name ? ' navbar-subtab--active' : ''}`}
            style={{ animationDelay: `${i * 30}ms` }}
          >
            <button className="navbar-subtab-rich__btn" onClick={() => onSelect(p.name)}>
              {p.failed && <i className="fa-solid fa-triangle-exclamation navbar-subtab-warn" />}
              {p.generating && <i className="fa-solid fa-spinner fa-spin navbar-subtab-spin" />}
              {p.name}
              <span className="navbar-subtab-ver">v{p.version}</span>
            </button>
            <ItemDotMenu><PageDotMenuItems p={p} /></ItemDotMenu>
            {showBar && (
              <div className="navbar-subtab-progress">
                <div className={`navbar-subtab-progress-fill ${p.generation_phase || ''}`}
                  style={{ width: `${pct}%` }} />
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

function InlineFlowItems({ flows, activeKey, onSelect }: {
  flows: FlowInfo[]; activeKey: string; onSelect: (persona: string) => void;
}) {
  return (
    <>
      {flows.map((f, i) => (
        <div
          key={f.id}
          className={`navbar-subtab-rich navbar-subtab-slide${activeKey === f.persona ? ' navbar-subtab--active' : ''}`}
          style={{ animationDelay: `${i * 30}ms` }}
        >
          <button className="navbar-subtab-rich__btn" onClick={() => onSelect(f.persona)}>
            <i className="fa-solid fa-user navbar-subtab-icon" />
            {f.persona}
            <span className="navbar-subtab-ver">v{f.version}</span>
          </button>
          <ItemDotMenu><FlowDotMenuItems f={f} /></ItemDotMenu>
        </div>
      ))}
    </>
  );
}

function InlineStaticItems({ items, activeKey, onSelect }: {
  items: SubTabDef[]; activeKey: string; onSelect: (key: string) => void;
}) {
  return (
    <>
      {items.map((st, i) => (
        <button
          key={st.key}
          className={`navbar-subtab navbar-subtab-slide${activeKey === st.key ? ' navbar-subtab--active' : ''}`}
          onClick={() => onSelect(st.key)}
          style={{ animationDelay: `${i * 30}ms` }}
        >
          {st.icon && <i className={st.icon} />}
          <span>{st.label}</span>
        </button>
      ))}
    </>
  );
}

/* ── Resolve which sub-tab items to show ──────────────────────── */

function SubItems({ activeTab, staticSubs, dyn }: {
  activeTab: RightPanelTab;
  staticSubs: { active: Record<string, string>; select: (t: string, k: string) => void };
  dyn: { pages: PageInfo[]; flows: FlowInfo[]; activePage: string; activeFlow: string };
}) {
  const sCfg = SUB_TAB_CONFIG[activeTab];

  if (sCfg) {
    return <InlineStaticItems items={sCfg.tabs}
      activeKey={staticSubs.active[activeTab] ?? sCfg.defaultKey}
      onSelect={(key) => staticSubs.select(activeTab, key)} />;
  }

  if (activeTab === 'design' && dyn.pages.length > 0) {
    return <InlinePageItems pages={dyn.pages} activeKey={dyn.activePage}
      onSelect={(n) => window.dispatchEvent(
        new globalThis.CustomEvent('select_design_page', { detail: n }),
      )} />;
  }

  return activeTab === 'user-flow' && dyn.flows.length > 0
    ? <InlineFlowItems flows={dyn.flows} activeKey={dyn.activeFlow}
      onSelect={(p) => window.dispatchEvent(
        new globalThis.CustomEvent('select_user_flow', { detail: p }),
      )} />
    : null;
}

/* ── Main component ────────────────────────────────────────────── */

function checkHasSubs(
  activeTab: RightPanelTab, dyn: { pages: PageInfo[]; flows: FlowInfo[] },
) {
  if (SUB_TAB_CONFIG[activeTab]) return true;
  if (activeTab === 'design' && dyn.pages.length > 0) return true;
  if (activeTab === 'user-flow' && dyn.flows.length > 0) return true;

  return false;
}

function useCollapseToggle(activeTab: RightPanelTab) {
  const [collapsedTab, setCollapsedTab] = useState<RightPanelTab | null>(null);
  const collapsed = collapsedTab === activeTab;

  const toggle = useCallback(() => {
    setCollapsedTab((prev) => (prev === activeTab ? null : activeTab));
  }, [activeTab]);

  return { collapsed, toggle };
}

function NavbarTabsView({ activeTab, compact, hasSubs, onToggle, staticSubs, dyn }: {
  activeTab: RightPanelTab; compact: boolean; hasSubs: boolean;
  onToggle: () => void;
  staticSubs: { active: Record<string, string>; select: (t: string, k: string) => void };
  dyn: { pages: PageInfo[]; flows: FlowInfo[]; activePage: string; activeFlow: string };
}) {
  const handleClick = useCallback((tabKey: string, isActive: boolean) => {
    if (isActive && hasSubs) { onToggle();

      return; }
    window.dispatchEvent(new globalThis.CustomEvent('navigate_to_tab', { detail: tabKey }));
  }, [hasSubs, onToggle]);

  return (
    <div className="navbar-tabs-wrapper">
      <div className={`navbar-tabs${compact ? ' navbar-tabs--compact' : ''}`}>
        {RIGHT_PANEL_TABS.map((tab) => (
          <TabItem key={tab.key} tab={tab} isActive={activeTab === tab.key}
            compact={compact} hasSubs={hasSubs} onTabClick={handleClick}
            activeTab={activeTab} staticSubs={staticSubs} dyn={dyn} />
        ))}
      </div>
    </div>
  );
}

function TabItem({ tab, isActive, compact, onTabClick, activeTab, staticSubs, dyn }: {
  tab: typeof RIGHT_PANEL_TABS[number]; isActive: boolean; compact: boolean;
  hasSubs: boolean; onTabClick: (key: string, active: boolean) => void;
  activeTab: RightPanelTab;
  staticSubs: { active: Record<string, string>; select: (t: string, k: string) => void };
  dyn: { pages: PageInfo[]; flows: FlowInfo[]; activePage: string; activeFlow: string };
}) {
  // Cumulative progress for design parent tab
  const designProgress = (() => {
    if (tab.key !== 'design') return null;
    const generating = dyn.pages.filter((p) => p.generating && p.sections_total > 0);

    if (generating.length === 0) return null;
    const totalSections = generating.reduce((s, p) => s + p.sections_total, 0);
    const doneSections = generating.reduce((s, p) => s + p.sections_done, 0);

    return totalSections > 0 ? Math.round((doneSections / totalSections) * 100) : 0;
  })();

  const tabBtn = (
    <button className={`navbar-tab${isActive ? ' navbar-tab--active' : ''}`}
      onClick={() => onTabClick(tab.key, isActive)}
      title={compact ? tab.label : undefined}>
      {tab.icon && <i className={`navbar-tab__icon ${tab.icon}`} />}
      <span className="navbar-tab__label">{tab.label}</span>
      {designProgress !== null && (
        <div className="navbar-tab-progress">
          <div className="navbar-tab-progress-fill" style={{ width: `${designProgress}%` }} />
        </div>
      )}
    </button>
  );

  if (isActive && compact) {
    return (
      <div className="navbar-tab-group--active">
        {tabBtn}
        <div className="navbar-tabs-divider" />
        <SubItems activeTab={activeTab} staticSubs={staticSubs} dyn={dyn} />
        {tab.key === 'design' && <DesignActions hasPages={dyn.pages.length > 0} />}
      </div>
    );
  }

  return tabBtn;
}

function useNavbarHooks() {
  return {
    activeTab: useActiveTab(),
    staticSubs: useStaticSubTabs(),
    dyn: useDynamicData(),
  };
}

function NavbarTabsInner() {
  const location = useLocation();
  const { staticSubs, dyn } = useNavbarHooks();

  if (location.pathname.startsWith('/planning')) {
    return null;
  }

  return location.pathname.startsWith('/projects/')
    ? <PublicNavbarTabsView staticSubs={staticSubs} dyn={dyn} />
    : null;
}
/* ── Public project page tabs (rendered in navbar for /projects/:id) ── */

type PublicTab = 'design' | 'requirements' | 'tasks' | 'architecture' | 'user-flow';

const PUBLIC_TAB_KEYS: PublicTab[] = ['design', 'requirements', 'tasks', 'user-flow', 'architecture'];

const PUBLIC_PROJECT_TABS: { key: PublicTab; label: string; icon: string }[] = [
  { key: 'design', label: 'Design', icon: 'fa-solid fa-palette' },
  { key: 'requirements', label: 'Reqs', icon: 'fa-solid fa-clipboard-list' },
  { key: 'tasks', label: 'Tasks', icon: 'fa-solid fa-tasks' },
  { key: 'user-flow', label: 'User Flow', icon: 'fa-solid fa-user' },
  { key: 'architecture', label: 'Architecture', icon: 'fa-solid fa-sitemap' },
];

function tabFromPath(pathname: string): PublicTab {
  // /projects/:id/design → 'design', /projects/:id/tasks → 'tasks', etc.
  const parts = pathname.split('/').filter(Boolean); // ['projects', id, tab?]
  const raw = parts[2]?.toLowerCase();

  return PUBLIC_TAB_KEYS.includes(raw as PublicTab) ? (raw as PublicTab) : 'design';
}

function usePublicCollapseToggle(tab: PublicTab) {
  const [collapsedTab, setCollapsedTab] = useState<PublicTab | null>(null);
  const collapsed = collapsedTab === tab;

  const toggle = useCallback(() => {
    setCollapsedTab((prev) => (prev === tab ? null : tab));
  }, [tab]);

  const reset = useCallback(() => setCollapsedTab(null), []);

  return { collapsed, toggle, reset };
}

function usePublicActiveTab(): [PublicTab, (t: PublicTab) => void, boolean, () => void] {
  const location = useLocation();
  const [tab, setTab] = useState<PublicTab>(() => tabFromPath(location.pathname));
  const { collapsed, toggle, reset } = usePublicCollapseToggle(tab);

  useEffect(() => {
    const h = (e: Event) => {
      const t = (e as globalThis.CustomEvent).detail as PublicTab;

      setTab(t);
      reset();
    };

    window.addEventListener('public_tab_select', h);

    return () => window.removeEventListener('public_tab_select', h);
  }, [reset]);

  const selectTab = useCallback((t: PublicTab) => {
    setTab(t);
    reset();
    window.dispatchEvent(new globalThis.CustomEvent('public_tab_select', { detail: t }));
    const parts = location.pathname.split('/').filter(Boolean);

    window.history.replaceState(null, '', `/${parts[0]}/${parts[1]}/${t}`);
  }, [location.pathname, reset]);

  return [tab, selectTab, collapsed, toggle];
}

function publicTabHasSubs(
  tab: PublicTab, dyn: { pages: PageInfo[]; flows: FlowInfo[] },
): boolean {
  if (tab === 'design') return dyn.pages.some(isPublicReadyPage);
  if (tab === 'user-flow') return dyn.flows.length > 0;

  return Boolean(SUB_TAB_CONFIG[tab]);
}

function isPublicReadyPage(page: PageInfo): boolean {
  return page.publicReady ?? Boolean(page.done && !page.planned && !page.generating && !page.failed);
}

function InlinePublicFlowItems({ flows, activeFlow }: {
  flows: FlowInfo[]; activeFlow: string;
}) {
  return (
    <>
      {flows.map((f, i) => (
        <button
          key={f.id}
          className={`navbar-subtab navbar-subtab-slide${activeFlow === f.persona ? ' navbar-subtab--active' : ''}`}
          onClick={() => window.dispatchEvent(
            new globalThis.CustomEvent('select_user_flow', { detail: f.persona }),
          )}
          style={{ animationDelay: `${i * 30}ms` }}
        >
          <i className="fa-solid fa-user navbar-subtab-icon" />
          {f.persona}
        </button>
      ))}
    </>
  );
}

function InlinePublicPageItems({ pages, activePage, projectId }: {
  pages: PageInfo[]; activePage: string; projectId: string;
}) {
  const readyPages = pages.filter(isPublicReadyPage);

  return (
    <>
      {readyPages.map((p, i) => (
        <button
          key={p.id}
          className={`navbar-subtab navbar-subtab-slide${activePage === p.name ? ' navbar-subtab--active' : ''}`}
          onClick={() => {
            window.dispatchEvent(new globalThis.CustomEvent('select_design_page', { detail: p.name }));
            window.history.replaceState(null, '', `/projects/${projectId}/design/${encodeURIComponent(p.name)}`);
          }}
          style={{ animationDelay: `${i * 30}ms` }}
        >
          {p.name}
        </button>
      ))}
    </>
  );
}

function PublicSubItems({ activeTab, staticSubs, dyn, projectId }: {
  activeTab: PublicTab;
  staticSubs: { active: Record<string, string>; select: (t: string, k: string) => void };
  dyn: { pages: PageInfo[]; flows: FlowInfo[]; activePage: string; activeFlow: string };
  projectId: string;
}) {
  if (activeTab === 'design' && dyn.pages.some(isPublicReadyPage)) {
    return <InlinePublicPageItems pages={dyn.pages} activePage={dyn.activePage} projectId={projectId} />;
  }
  if (activeTab === 'user-flow' && dyn.flows.length > 0) {
    return <InlinePublicFlowItems flows={dyn.flows} activeFlow={dyn.activeFlow} />;
  }
  if (SUB_TAB_CONFIG[activeTab]) {
    let items = SUB_TAB_CONFIG[activeTab].tabs;

    if (activeTab === 'tasks') {
      items = items.filter((st) => st.key !== 'kanban');
    }

    return (
      <InlineStaticItems
        items={items}
        activeKey={staticSubs.active[activeTab] ?? SUB_TAB_CONFIG[activeTab].defaultKey}
        onSelect={(key) => staticSubs.select(activeTab, key)}
      />
    );
  }

  return null;
}

function PublicNavbarTabsView({ staticSubs, dyn }: {
  staticSubs: { active: Record<string, string>; select: (t: string, k: string) => void };
  dyn: { pages: PageInfo[]; flows: FlowInfo[]; activePage: string; activeFlow: string };
}) {
  const location = useLocation();
  const [activeTab, setActiveTab, collapsed, toggle] = usePublicActiveTab();
  const hasSubs = publicTabHasSubs(activeTab, dyn);
  const showSubs = hasSubs && !collapsed;
  const projectId = location.pathname.split('/').filter(Boolean)[1] || '';

  return (
    <div className="navbar-tabs-wrapper navbar-tabs-wrapper--public">
      <div className={`navbar-tabs${showSubs ? ' navbar-tabs--compact' : ''}`}>
        {PUBLIC_PROJECT_TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          const href = `/projects/${projectId}/${tab.key}`;

          const tabLink = (
            <a
              className={`navbar-tab${isActive ? ' navbar-tab--active' : ''}`}
              href={href}
              onClick={(e) => {
                e.preventDefault();
                if (isActive && hasSubs) { toggle(); } else { setActiveTab(tab.key); }
              }}
              title={showSubs ? tab.label : undefined}
            >
              <i className={`navbar-tab__icon ${tab.icon}`} />
              <span className="navbar-tab__label">{tab.label}</span>
            </a>
          );

          if (isActive && showSubs) {
            return (
              <div key={tab.key} className="navbar-tab-group--active">
                {tabLink}
                <div className="navbar-tabs-divider" />
                <PublicSubItems activeTab={activeTab} staticSubs={staticSubs} dyn={dyn} projectId={projectId} />
              </div>
            );
          }

          return <React.Fragment key={tab.key}>{tabLink}</React.Fragment>;
        })}
      </div>
    </div>
  );
}


export const NavbarTabs: React.FC = () => <NavbarTabsInner />;
